/**
 * Per-slot insert FX stacks (stereo, after volume, before strip pan).
 */
import type { FxNode } from "../types";
import { DEFAULT_INSTRUMENT_SLOT_COUNT, clampInstrumentSlotCount } from "../types";
import { deepClone } from "./deepClone";
import { normalizeFxNode } from "./fxChain";

export function createDefaultSlotFxStacks(
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): FxNode[][] {
  const n = clampInstrumentSlotCount(count);
  return Array.from({ length: n }, () => []);
}

export function normalizeSlotFxStack(raw: unknown): FxNode[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(node => {
    if (!node || typeof node !== "object" || !("type" in node)) {
      return normalizeFxNode({ type: "eq" });
    }
    return normalizeFxNode(node as Partial<FxNode> & Pick<FxNode, "type">);
  });
}

export function normalizeSlotFxStacks(
  raw: unknown,
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): FxNode[][] {
  const n = clampInstrumentSlotCount(count);
  const list = Array.isArray(raw) ? raw : [];
  return Array.from({ length: n }, (_, i) => normalizeSlotFxStack(list[i]));
}

export function cloneSlotFxStack(stack: FxNode[]): FxNode[] {
  return stack.map(fx => deepClone(fx));
}

export function cloneSlotFxStacks(stacks: FxNode[][]): FxNode[][] {
  return stacks.map(cloneSlotFxStack);
}

export function patchSlotFxStack(
  stacks: FxNode[][],
  slot: number,
  effects: FxNode[],
): FxNode[][] {
  const n = Math.max(stacks.length, slot + 1);
  const next = cloneSlotFxStacks(normalizeSlotFxStacks(stacks, n));
  next[slot] = normalizeSlotFxStack(effects);
  return next;
}
